<script setup lang="ts">
import { computed, ref } from 'vue';

import { AppButton } from '../../shared/ui';
import { useAuthStore } from '../../stores/auth';

const auth = useAuthStore();
const dismissed = ref(sessionStorage.getItem('verification-banner-dismissed') === '1');
const resending = ref(false);
const flash = ref<string | null>(null);
const error = ref<string | null>(null);

const show = computed(
  () =>
    !!auth.user &&
    !auth.user.emailVerified &&
    !auth.user.workspaceSuspended &&
    auth.user.verificationDeadline !== null &&
    !dismissed.value,
);

const daysLeft = computed(() => {
  const deadline = auth.user?.verificationDeadline;
  if (!deadline) return 0;
  const ms = new Date(deadline).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
});

function dismiss(): void {
  dismissed.value = true;
  sessionStorage.setItem('verification-banner-dismissed', '1');
}

async function onResend(): Promise<void> {
  resending.value = true;
  error.value = null;
  flash.value = null;
  try {
    await auth.resendVerification();
    flash.value = 'Письмо отправлено';
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Не удалось отправить письмо';
  } finally {
    resending.value = false;
  }
}
</script>

<template>
  <div
    v-if="show"
    class="verification-banner"
    role="status"
  >
    <p class="verification-banner__text">
      Подтвердите почту в течение {{ daysLeft }} дн.
    </p>
    <div class="verification-banner__actions">
      <AppButton
        type="button"
        variant="primary"
        class="verification-banner__btn"
        :loading="resending"
        @click="onResend"
      >
        Отправить письмо снова
      </AppButton>
      <button
        type="button"
        class="verification-banner__dismiss"
        aria-label="Скрыть"
        @click="dismiss"
      >
        ×
      </button>
    </div>
    <p
      v-if="flash"
      class="verification-banner__flash"
    >
      {{ flash }}
    </p>
    <p
      v-if="error"
      class="verification-banner__error"
    >
      {{ error }}
    </p>
  </div>
</template>

<style scoped>
.verification-banner {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.65rem 1rem;
  padding: 0.55rem 1rem;
  background: color-mix(in srgb, var(--warning-glow) 70%, var(--surface));
  border-bottom: 1px solid color-mix(in srgb, var(--warning) 25%, transparent);
}

.verification-banner__text {
  margin: 0;
  font-size: 0.82rem;
  color: var(--text);
}

.verification-banner__actions {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.verification-banner__btn {
  font-size: 0.78rem;
  padding: 0.25rem 0.65rem;
}

.verification-banner__dismiss {
  border: none;
  background: transparent;
  color: var(--muted);
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 0.25rem;
}

.verification-banner__flash,
.verification-banner__error {
  flex-basis: 100%;
  margin: 0;
  font-size: 0.75rem;
  text-align: center;
}

.verification-banner__error {
  color: var(--danger);
}
</style>
